/* facets.js — AJAX filtering for the filter drawer (snippets/facets.liquid).
 *
 * Trimmed from Dawn: this theme only renders the drawer form
 * (#FacetFiltersFormMobile), so the desktop/vertical/pills form handling is
 * gone. Flow: any input/change/submit in the form -> debounce -> fetch the
 * section with the new query string -> swap the product grid, the product
 * count, the filter groups and the active-pill bars -> pushState the URL.
 *
 * Depends on global.js for debounce() and the <menu-drawer> element that
 * opens/closes the drawer. sc-category-tree.js drives the hidden
 * gender/category checkboxes and dispatches a change event into this form.
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onFacetChange = debounce(this.onSubmitHandler.bind(this), 500);

    const form = this.querySelector('form');
    form.addEventListener('input', this.onFacetChange);
    form.addEventListener('change', this.onFacetChange);
    form.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });

    // Close (X) and Apply buttons close the drawer the same way the trigger
    // does, so <menu-drawer> restores focus and the body scroll lock.
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-facets-close]');
      if (!button) return;
      const summary = button.closest('.mobile-facets__wrapper')?.querySelector('summary');
      if (summary) summary.click();
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sectionId = document.getElementById('product-grid').dataset.id;
    const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;

    document.querySelectorAll('.facets-container .loading__spinner').forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    document.getElementById('ProductCount').classList.add('loading');

    const cached = FacetFiltersForm.filterData.find((entry) => entry.url === url);
    const html = cached ? Promise.resolve(cached.html) : FacetFiltersForm.fetchSection(url);
    html.then((text) => FacetFiltersForm.renderSection(text, event));

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static fetchSection(url) {
    return fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData.push({ html, url });
        return html;
      });
  }

  static renderSection(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
  }

  static renderProductGridContainer(parsedHTML) {
    const container = document.getElementById('ProductGridContainer');
    // Lock height to prevent layout collapse during innerHTML swap
    container.style.minHeight = container.offsetHeight + 'px';

    container.innerHTML = parsedHTML.getElementById('ProductGridContainer').innerHTML;

    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    // Reveal product cards after AJAX — Webflow animations set opacity:0 on
    // initial render and animate to 1 on page load, but the animation engine
    // does not re-scan after innerHTML replacement.
    container.querySelectorAll('[data-w-id][style*="opacity"]').forEach((element) => {
      element.style.opacity = '1';
    });

    // Release the height lock once the new content has been laid out.
    requestAnimationFrame(() => {
      container.style.minHeight = '';
    });
  }

  static renderProductCount(parsedHTML) {
    const container = document.getElementById('ProductCount');
    container.innerHTML = parsedHTML.getElementById('ProductCount').innerHTML;
    container.classList.remove('loading');
    document.querySelectorAll('.facets-container .loading__spinner').forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(parsedHTML, event) {
    const selector = '#FacetFiltersFormMobile .js-filter';
    const facetsFromFetch = Array.from(parsedHTML.querySelectorAll(selector));
    const facetsFromDom = Array.from(document.querySelectorAll(selector));

    // Remove facets that are no longer returned from the server
    facetsFromDom.forEach((currentElement) => {
      if (!facetsFromFetch.some(({ id }) => currentElement.id === id)) currentElement.remove();
    });

    // The group the user is interacting with only gets its value list swapped
    // (keeps the open state, scroll position and any typed price); every
    // other group is replaced wholesale.
    const activeFilter = event ? event.target.closest('.js-filter') : null;
    const activeId = activeFilter ? activeFilter.id : null;

    facetsFromFetch.forEach((elementToRender, index) => {
      if (elementToRender.id === activeId) return;
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else if (index > 0) {
        document.getElementById(facetsFromFetch[index - 1].id)?.after(elementToRender);
      } else {
        document.querySelector(selector)?.before(elementToRender);
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    const activeFromFetch = activeId ? facetsFromFetch.find(({ id }) => id === activeId) : null;
    if (activeFromFetch) {
      const target = document.getElementById(activeId);
      const sourceList = activeFromFetch.querySelector('.mobile-facets__list');
      const targetList = target.querySelector('.mobile-facets__list');
      if (sourceList && targetList) targetList.outerHTML = sourceList.outerHTML;

      // If the checkbox that was clicked has just been replaced, put focus back
      // on its replacement (or the group heading) so keyboard users stay in
      // place instead of dropping to <body>.
      if (!document.body.contains(event.target)) {
        const focusTarget = document.getElementById(event.target.id) || target.querySelector('.mobile-facets__summary');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }
    }
  }

  static renderActiveFacets(parsedHTML) {
    ['.active-facets-mobile', '.active-facets-drawer'].forEach((selector) => {
      const activeFacetsElement = parsedHTML.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });
  }

  static renderAdditionalElements(parsedHTML) {
    ['.mobile-facets__open', '.mobile-facets__count'].forEach((selector) => {
      const element = parsedHTML.querySelector(selector);
      if (!element) return;
      document.querySelector(selector).innerHTML = element.innerHTML;
    });

    const menuDrawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    if (menuDrawer) menuDrawer.bindEvents();
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  onSubmitHandler(event) {
    event.preventDefault();
    // Read this element's own form, not event.target.closest('form'): the
    // debounce can fire after an AJAX render has replaced the input that
    // raised the event, and a detached input has no form.
    const searchParams = this.createSearchParams(this.querySelector('form'));
    if (searchParams === FacetFiltersForm.searchParamsPrev) return;
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const href = event.currentTarget.href;
    const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.querySelectorAll('input');
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
